import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Receipt, ArrowsClockwise, Lightning, Truck, House, FileText, CheckCircle,
  Calendar, User, ArrowSquareOut, Clock, MapPin, PencilSimple, ClockCounterClockwise,
} from "@phosphor-icons/react";
import DraftInvoiceEditor from "./DraftInvoiceEditor";

const PRIORITY_STYLE = {
  NORMAL: "bg-[#D4E0D1] text-[#2D5A27]",
  HIGH: "bg-[#F9E4B7] text-[#7A5A11]",
  URGENT: "bg-[#F4C7B9] text-[#7A2E18]",
  EMERGENCY: "bg-[#F4C7B9] text-[#7A2E18]",
  LOW: "bg-[#F7F6F2] text-[#8A8780] border border-[#E5E2D9]",
};

export default function InvoicesPending() {
  const [tab, setTab] = useState("drafts"); // drafts | pending
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [busy, setBusy] = useState({});
  const [filter, setFilter] = useState("all");
  const [trailFor, setTrailFor] = useState(null); // invoice id
  const [trail, setTrail] = useState(null);
  const [editFor, setEditFor] = useState(null); // draft invoice id opened in editor

  const load = async () => {
    try {
      const [r, d] = await Promise.all([
        api.get("/finance/pending-invoices"),
        api.get("/finance/draft-invoices"),
      ]);
      setRows(r.data || []);
      setDrafts(d.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter(r => {
    if (filter === "URGENT" || filter === "HIGH" || filter === "NORMAL") return r.priority === filter;
    if (filter === "drop" || filter === "home_service") return r.service_category_type === filter;
    return true;
  });

  // Iter 136 fix — SR-pending "edit" was navigating to
  // /finance/invoices?new=1&sr=<id>, but the Invoices page never read
  // those query params, so nothing happened. Now we create the draft
  // in-place (via the same from-request endpoint used by "Create
  // Invoice") and immediately open the DraftInvoiceEditor so office
  // staff can adjust weights / charges before finalising.
  const editPending = async (rid) => {
    setBusy(b => ({ ...b, [rid]: true }));
    try {
      const res = await api.post(`/finance/invoices/from-request/${rid}`);
      toast.success(`Draft ${res.data.invoice_no} ready to edit`);
      await load();
      setTab("drafts");
      setEditFor(res.data.id);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(b => ({ ...b, [rid]: false }));
  };

  const generate = async (rid) => {
    if (!window.confirm("Auto-generate the invoice for this completed service request?")) return;
    setBusy(b => ({ ...b, [rid]: true }));
    try {
      const res = await api.post(`/finance/invoices/from-request/${rid}`);
      toast.success(`Invoice ${res.data.invoice_no} created · ₹${Number(res.data.total).toFixed(2)}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(b => ({ ...b, [rid]: false }));
  };

  const finalize = async (iid) => {
    const remarks = window.prompt("Approve & finalize this draft — optional remarks?", "") || "";
    setBusy(b => ({ ...b, [iid]: true }));
    try {
      const res = await api.post(`/finance/draft-invoices/${iid}/finalize`, { notes: remarks || null });
      toast.success(`Invoice ${res.data.invoice_no} finalized · ₹${Number(res.data.total).toFixed(2)}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(b => ({ ...b, [iid]: false }));
  };

  const reject = async (iid) => {
    const remarks = window.prompt(
      "Reject this draft — the field staff will be notified.\n\nWhat should they fix?",
      "",
    );
    if (!remarks || remarks.trim().length < 3) {
      if (remarks !== null) toast.error("Please enter at least 3 characters of feedback");
      return;
    }
    setBusy(b => ({ ...b, [iid]: true }));
    try {
      await api.post(`/finance/invoices/${iid}/reject`, { remarks: remarks.trim() });
      toast.success("Draft rejected — field staff notified");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(b => ({ ...b, [iid]: false }));
  };

  const openTrail = async (iid) => {
    setTrailFor(iid); setTrail(null);
    try {
      const r = await api.get(`/finance/invoices/${iid}/audit-trail`);
      setTrail(r.data);
    } catch (e) { toast.error(formatApiError(e)); setTrailFor(null); }
  };

  return (
    <AppShell
      title="Invoice Pending"
      subtitle={`${drafts.length} field draft${drafts.length === 1 ? "" : "s"} · ${rows.length} request${rows.length === 1 ? "" : "s"} unbilled`}
      actions={
        <div className="flex gap-2">
          <Link to="/finance/invoices" data-testid="ip-go-invoices"
                className="h-10 px-3 inline-flex items-center gap-2 border border-[#E5E2D9] rounded-md text-sm hover:bg-[#F7F6F2]">
            <FileText size={14}/>All Invoices
          </Link>
          <button onClick={load} data-testid="ip-refresh"
                  className="h-10 px-3 inline-flex items-center gap-2 border border-[#E5E2D9] rounded-md text-sm hover:bg-[#F7F6F2]">
            <ArrowsClockwise size={14}/>Refresh
          </button>
        </div>
      }
    >
      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-[#E5E2D9]" data-testid="ip-tabs">
        <TabBtn active={tab === "drafts"} onClick={() => setTab("drafts")}
                count={drafts.length} testid="ip-tab-drafts" Icon={Truck}>
          Field-staff drafts
        </TabBtn>
        <TabBtn active={tab === "pending"} onClick={() => setTab("pending")}
                count={rows.length} testid="ip-tab-sr-pending" Icon={Receipt}>
          SR-pending invoices
        </TabBtn>
      </div>

      {tab === "drafts" && (
        <DraftsTable drafts={drafts} busy={busy} onFinalize={finalize} onReject={reject}
                     onTrail={openTrail} onEdit={(id) => setEditFor(id)} />
      )}

      {tab === "pending" && (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-4 gap-3 mb-5">
            {[
              { l: "Total Pending", v: rows.length, I: Receipt, key: "all" },
              { l: "Urgent",        v: rows.filter(r => r.priority === "URGENT" || r.priority === "EMERGENCY").length, I: Lightning, key: "URGENT" },
              { l: "High",          v: rows.filter(r => r.priority === "HIGH").length, I: Lightning, key: "HIGH" },
              { l: "Normal",        v: rows.filter(r => r.priority === "NORMAL").length, I: Clock, key: "NORMAL" },
            ].map(k => (
              <button key={k.l} onClick={() => setFilter(k.key)} data-testid={`ip-kpi-${k.key}`}
                      className={`text-left p-4 rounded-md border transition ${filter === k.key ? "border-[#2D5A27] bg-[#EFF3EE]" : "border-[#E5E2D9] bg-white hover:bg-[#F7F6F2]"}`}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] inline-flex items-center gap-1.5">
                  <k.I size={11}/>{k.l}
                </div>
                <div className="text-2xl font-light tabular-nums mt-1">{k.v}</div>
              </button>
            ))}
          </div>

          <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden">
            <div className="px-5 py-3 border-b border-[#E5E2D9] flex items-center gap-2">
              <Receipt size={14}/>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">Pending list</div>
              <div className="ml-auto text-[11px] text-[#8A8780]">
                {filter !== "all" && <button onClick={() => setFilter("all")} className="underline mr-2">clear filter</button>}
                {filtered.length} shown
              </div>
            </div>
            {filtered.length === 0 ? (
              <div className="p-12 text-center text-sm text-[#8A8780]">
                <CheckCircle size={28} className="inline-block mb-2 opacity-40"/><br/>
                All clear. No completed services awaiting an invoice.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="ip-table">
                  <thead className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
                    <tr>
                      <th className="text-left px-5 py-2.5">Request</th>
                      <th className="text-left px-3 py-2.5">Customer</th>
                      <th className="text-left px-3 py-2.5">Service</th>
                      <th className="text-left px-3 py-2.5">Package</th>
                      <th className="text-left px-3 py-2.5">Priority</th>
                      <th className="text-left px-3 py-2.5">Staff</th>
                      <th className="text-right px-3 py-2.5">Date</th>
                      <th className="text-right px-5 py-2.5">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(r => (
                      <tr key={r.id} className="border-t border-[#F0ECE3]" data-testid={`ip-row-${r.id}`}>
                        <td className="px-5 py-3 font-mono text-[11px] text-[#2D5A27]">{r.request_no}</td>
                        <td className="px-3 py-3">
                          <Link to={`/customers/${r.customer_id}`} className="hover:underline inline-flex items-center gap-1">
                            {r.customer_name}<ArrowSquareOut size={10}/>
                          </Link>
                          <div className="text-[10px] font-mono text-[#8A8780]">{r.customer_code}</div>
                        </td>
                        <td className="px-3 py-3">
                          <span className="inline-flex items-center gap-1 text-[11px]">
                            {r.service_category_type === "drop" ? <Truck size={11}/> : <House size={11}/>}
                            {r.service_category_type === "drop" ? "Drop" : "Home"}
                          </span>
                          {r.waste_category && <div className="text-[10px] text-[#8A8780]">{r.waste_category}</div>}
                        </td>
                        <td className="px-3 py-3 text-[12px]">{r.package_name || "—"}</td>
                        <td className="px-3 py-3">
                          <span className={`text-[9px] px-2 py-0.5 rounded uppercase tracking-wider ${PRIORITY_STYLE[r.priority] || "bg-[#E5E2D9]"}`}>{r.priority}</span>
                        </td>
                        <td className="px-3 py-3 text-[11px]">
                          {r.assigned_staff_name ? (
                            <span className="inline-flex items-center gap-1"><User size={10}/>{r.assigned_staff_name}</span>
                          ) : "—"}
                        </td>
                        <td className="px-3 py-3 text-right text-[11px] text-[#8A8780]">
                          <div className="inline-flex items-center gap-1"><Calendar size={10}/>{(r.completed_at || r.scheduled_date || r.preferred_date || "").slice(0, 10)}</div>
                        </td>
                        <td className="px-5 py-3 text-right whitespace-nowrap">
                          <Link to={`/operations/service-requests/${r.id}`}
                                data-testid={`ip-sr-open-${r.id}`}
                                title="Open service request"
                                className="h-8 w-8 inline-flex items-center justify-center border border-[#E5E2D9] rounded hover:bg-[#F7F6F2] mr-1">
                            <ArrowSquareOut size={14}/>
                          </Link>
                          <button onClick={() => editPending(r.id)}
                                disabled={busy[r.id]}
                                data-testid={`ip-sr-edit-${r.id}`}
                                title="Create draft and open editor to adjust weights / charges"
                                className="h-8 w-8 inline-flex items-center justify-center border border-[#E5E2D9] rounded hover:bg-[#F7F6F2] disabled:opacity-50 mr-1">
                            <PencilSimple size={14}/>
                          </button>
                          <button onClick={() => generate(r.id)} disabled={busy[r.id]}
                                  data-testid={`ip-create-${r.id}`}
                                  className="h-8 px-3 inline-flex items-center gap-1.5 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded text-[11px] font-medium">
                            <FileText size={12}/>{busy[r.id] ? "Creating…" : "Create Invoice"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
      {editFor && (
        <DraftInvoiceEditor
          invoiceId={editFor}
          onClose={() => setEditFor(null)}
          onSaved={load}
        />
      )}
      {trailFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
              onClick={() => { setTrailFor(null); setTrail(null); }}
              data-testid="ip-trail-modal">          <div className="bg-white w-full max-w-lg rounded-lg p-5 max-h-[85vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">Audit trail</div>
                <div className="font-heading text-lg">{trail?.invoice_no || "…"}</div>
                {trail?.status && (
                  <span className="text-[10px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded bg-[#F7F6F2] text-[#5C5A55]">
                    {trail.status}
                  </span>
                )}
              </div>
              <button onClick={() => { setTrailFor(null); setTrail(null); }}
                       className="p-1.5 rounded hover:bg-[#F7F6F2]">×</button>
            </div>
            {!trail && <div className="text-sm text-[#8A8780]">Loading…</div>}
            {trail && trail.trail.length === 0 && (
              <div className="text-sm text-[#8A8780]">No audit entries yet.</div>
            )}
            {trail && trail.trail.map((row, i) => (
              <div key={i} className="border-l-2 border-[#E5E2D9] pl-3 pb-4 relative" data-testid={`ip-trail-row-${i}`}>
                <div className="absolute -left-[5px] top-0 w-2 h-2 rounded-full bg-[#2D5A27]"/>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#8A8780]">
                  {(row.at || "").replace("T", " ").slice(0, 19)}
                </div>
                <div className="text-sm font-medium">
                  {row.action.replace(/^(finance\.|staff\.|invoice\.)/, "").replace(/_/g, " ")}
                </div>
                <div className="text-xs text-[#5C5A55]">
                  by <strong>{row.actor_name || "—"}</strong>
                  {row.actor_role && <span className="text-[#8A8780]"> · {row.actor_role}</span>}
                </div>
                {row.meta && Object.keys(row.meta).length > 0 && (
                  <div className="mt-1 text-[11px] text-[#5C5A55] font-mono bg-[#F7F6F2] px-2 py-1.5 rounded">
                    {Object.entries(row.meta).filter(([, v]) => v !== null && v !== undefined && v !== "").map(([k, v]) => (
                      <div key={k}><span className="text-[#8A8780]">{k}:</span> {String(v)}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </AppShell>
  );
}

const TabBtn = ({ active, onClick, count, children, testid, Icon }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-2.5 text-xs uppercase tracking-[0.18em] inline-flex items-center gap-1.5 border-b-2 transition ${active ? "border-[#2D5A27] text-[#2D5A27]" : "border-transparent text-[#5C5A55] hover:text-[#1A1A1A]"}`}
  >
    <Icon size={12} />
    {children}
    <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${active ? "bg-[#2D5A27] text-white" : "bg-[#E5E2D9] text-[#5C5A55]"}`}>{count}</span>
  </button>
);

const DraftsTable = ({ drafts, busy, onFinalize, onReject, onTrail, onEdit }) => {
  if (drafts.length === 0) {
    return (
      <div className="bg-white border border-[#E5E2D9] rounded-md p-12 text-center" data-testid="ip-no-drafts">
        <Truck size={28} className="inline-block mb-2 opacity-40" />
        <div className="text-sm text-[#5C5A55]">No field-staff drafts awaiting approval</div>
        <div className="text-[11px] text-[#8A8780] mt-1">
          Field-generated invoices will appear here for review before finalization.
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden">
      <div className="px-5 py-3 border-b border-[#E5E2D9] flex items-center gap-2">
        <Truck size={14}/>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">Field drafts</div>
        <div className="ml-auto text-[11px] text-[#8A8780]">{drafts.length} awaiting</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="ip-drafts-table">
          <thead className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
            <tr>
              <th className="text-left px-5 py-2.5">Invoice #</th>
              <th className="text-left px-3 py-2.5">Customer</th>
              <th className="text-left px-3 py-2.5">Field staff</th>
              <th className="text-left px-3 py-2.5">Bags</th>
              <th className="text-right px-3 py-2.5">Total</th>
              <th className="text-right px-3 py-2.5">Created</th>
              <th className="text-right px-5 py-2.5">Action</th>
            </tr>
          </thead>
          <tbody>
            {drafts.map((d) => (
              <tr key={d.id} className="border-t border-[#F0ECE3]" data-testid={`ip-draft-${d.id}`}>
                <td className="px-5 py-3 font-mono text-[11px] text-[#2D5A27]">
                  {d.invoice_no}
                  {d.service_request_no && (
                    <div className="text-[9px] text-[#8A8780]">{d.service_request_no}</div>
                  )}
                </td>
                <td className="px-3 py-3">
                  <Link to={`/customers/${d.customer_id}`} className="hover:underline inline-flex items-center gap-1">
                    {d.customer?.name || d.customer_name || "—"}<ArrowSquareOut size={10}/>
                  </Link>
                  {d.customer?.phone && (
                    <div className="text-[10px] font-mono text-[#8A8780]">{d.customer.phone}</div>
                  )}
                  {(d.source === "pos_field" || d.source === "pos") && (
                    <span className="inline-flex items-center gap-1 mt-1 text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded bg-[#F9E4B7]/60 text-[#7A5A11] font-semibold" data-testid={`ip-source-pos-${d.id}`}>
                      Field POS
                    </span>
                  )}
                </td>
                <td className="px-3 py-3 text-[11px]">
                  <span className="inline-flex items-center gap-1"><User size={10}/>{d.field_staff_name || d.created_by_name || "—"}</span>
                  {d.field_gps && (
                    <div className="text-[9px] text-[#8A8780] inline-flex items-center gap-1 mt-0.5">
                      <MapPin size={9}/>{Number(d.field_gps.latitude).toFixed(4)}, {Number(d.field_gps.longitude).toFixed(4)}
                    </div>
                  )}
                </td>
                <td className="px-3 py-3 text-[11px]">
                  {d.field_bag_codes?.length || 0} bag{d.field_bag_codes?.length === 1 ? "" : "s"}
                  {d.field_bag_type && (
                    <div className="text-[9px] text-[#8A8780] uppercase">{d.field_bag_type}</div>
                  )}
                </td>
                <td className="px-3 py-3 text-right font-mono text-[12px] font-semibold">₹{Number(d.total || 0).toFixed(2)}</td>
                <td className="px-3 py-3 text-right text-[11px] text-[#8A8780]">
                  <div className="inline-flex items-center gap-1"><Calendar size={10}/>{(d.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <Link to={`/finance/invoices?id=${d.id}`}
                        data-testid={`ip-open-${d.id}`}
                        title="Open invoice details"
                        className="h-8 w-8 inline-flex items-center justify-center border border-[#E5E2D9] rounded hover:bg-[#F7F6F2] mr-1">
                    <ArrowSquareOut size={14}/>
                  </Link>
                  <button onClick={() => onEdit(d.id)}
                          data-testid={`ip-edit-${d.id}`}
                          title="Open & edit draft — adjust weights / charges"
                          className="h-8 w-8 inline-flex items-center justify-center border border-[#E5E2D9] rounded hover:bg-[#F7F6F2] mr-1">
                    <PencilSimple size={14}/>
                  </button>
                  <button onClick={() => onTrail(d.id)}
                          data-testid={`ip-trail-${d.id}`}
                          title="View audit trail"
                          className="h-8 w-8 inline-flex items-center justify-center border border-[#E5E2D9] rounded hover:bg-[#F7F6F2] mr-1">
                    <ClockCounterClockwise size={14}/>
                  </button>
                  <button onClick={() => onReject(d.id)} disabled={busy[d.id]}
                          data-testid={`ip-reject-${d.id}`}
                          className="h-8 px-3 inline-flex items-center gap-1.5 border border-[#C25D38] text-[#C25D38] hover:bg-[#C25D38]/10 disabled:opacity-50 rounded text-[11px] font-medium mr-1">
                    Reject
                  </button>
                  <button onClick={() => onFinalize(d.id)} disabled={busy[d.id]}
                          data-testid={`ip-finalize-${d.id}`}
                          className="h-8 px-3 inline-flex items-center gap-1.5 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded text-[11px] font-medium">
                    <CheckCircle size={12}/>{busy[d.id] ? "Approving…" : "Approve"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
